import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ClientServicesService } from 'src/app/services/admin/client-services.service';
import { SnackService } from 'src/app/services/snack.service';

@Component({
  selector: 'app-set-up-rates',
  templateUrl: './set-up-rates.component.html',
  styleUrls: ['./set-up-rates.component.scss'],
})
export class SetUpRatesComponent implements OnInit {
  form: UntypedFormGroup;
  keyValuePairsTab1: { key: UntypedFormControl; value: UntypedFormControl }[] = [];
  keyValuePairsTab2: { key: UntypedFormControl; value: UntypedFormControl }[] = [];
  keyValuePairsTab3: { key: UntypedFormControl; value: UntypedFormControl }[] = [];
  showAddForm = { billable: false, medicare: false, stripe: false };
  isLoading = false;
  selectedTabIndex = 0;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    private snackService: SnackService,
    private dialogRef: MatDialogRef<SetUpRatesComponent>,
    private fb: UntypedFormBuilder,
    private clientService: ClientServicesService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      billable: this.fb.group(this.createBillableControls()),
      medicare: this.fb.group(this.createMedicareControls()),
      stripe: this.fb.group(this.createStripeControls()),
    });
  }

  createBillableControls(): { [key: string]: UntypedFormControl } {
    const billableControls = {};
    if (this.data.client.rates.billable) {
      Object.keys(this.data.client.rates.billable).forEach((key) => {
        billableControls[key] = [this.data.client.rates.billable[key]];
      });
    }
    return billableControls;
  }

  createMedicareControls(): { [key: string]: UntypedFormControl } {
    const medicareControls = {};
    if (this.data.client.rates.medicare) {
      Object.keys(this.data.client.rates.medicare).forEach((key) => {
        medicareControls[key] = [this.data.client.rates.medicare[key]];
      });
    }
    return medicareControls;
  }

  createStripeControls(): { [key: string]: UntypedFormControl } {
    const stripeControls = {};
    if (this.data.client.rates.stripe) {
      Object.keys(this.data.client.rates.stripe).forEach((key) => {
        stripeControls[key] = this.fb.group({
          price_id: [this.data.client.rates.stripe[key]?.price_id || ''],
          active: [this.data.client.rates.stripe[key].active],
        });
      });
    }
    return stripeControls;
  }

  get billableControls() {
    return (this.form.get('billable') as UntypedFormGroup).controls;
  }

  get medicareControls() {
    return (this.form.get('medicare') as UntypedFormGroup).controls;
  }

  get stripeControls() {
    return (this.form.get('stripe') as UntypedFormGroup).controls;
  }

  get objectKeys() {
    return Object.keys.bind(Object);
  }

  deleteBillable(index: string): void {
    (this.form.get('billable') as UntypedFormGroup).removeControl(index);
  }

  deleteMedicare(index: string): void {
    (this.form.get('medicare') as UntypedFormGroup).removeControl(index);
  }

  deleteStripe(index: string): void {
    (this.form.get('stripe') as UntypedFormGroup).removeControl(index);
  }

  showForm(tab: string): void {
    this.showAddForm[tab] = true;
    this.addInputs(tab);
  }

  addInputs(tab: string): void {
    const keyControl = new UntypedFormControl('');
    const valueControl = new UntypedFormControl('');

    if (tab === 'billable') {
      this.keyValuePairsTab1.push({ key: keyControl, value: valueControl });
    } else if (tab === 'medicare') {
      this.keyValuePairsTab2.push({ key: keyControl, value: valueControl });
    } else if (tab === 'stripe') {
      this.keyValuePairsTab3.push({ key: keyControl, value: valueControl });
    }
  }

  addControlsForm(keyValuePairs: { key: UntypedFormControl; value: UntypedFormControl }[], tab: string): void {
    keyValuePairs.forEach((pair, index) => {
      const key = pair.key.value;
      let value = pair.value.value;
      if (tab === 'billable') {
        value = Number(value);
        const billableFormGroup = this.form.get('billable') as UntypedFormGroup;
        billableFormGroup.addControl(key, this.fb.control(value));
      } else if (tab === 'medicare') {
        value = Number(value);
        const medicareFormGroup = this.form.get('medicare') as UntypedFormGroup;
        medicareFormGroup.addControl(key, this.fb.control(value));
      } else if (tab === 'stripe') {
        const stripeFormGroup = this.form.get('stripe') as UntypedFormGroup;
        stripeFormGroup.addControl(
          key,
          this.fb.group({
            price_id: [value],
            active: [true],
          })
        );
      }
    });
  }

  save(tab: string): void {
    let client;
    if (tab === 'billable') {
      this.addControlsForm(this.keyValuePairsTab1, tab);
      const form = this.form.value;
      client = {
        rates: {
          ...this.data.client.rates,
          billable: form.billable,
        },
      };
      this.keyValuePairsTab1.forEach((pair) => {
        pair.key.setValue('');
        pair.value.setValue('');
      });
      this.keyValuePairsTab1 = [];
    } else if (tab === 'medicare') {
      this.addControlsForm(this.keyValuePairsTab2, tab);
      const form = this.form.value;
      client = {
        rates: {
          ...this.data.client.rates,
          medicare: form.medicare,
        },
      };
      this.keyValuePairsTab2.forEach((pair) => {
        pair.key.setValue('');
        pair.value.setValue('');
      });
      this.keyValuePairsTab2 = [];
    } else if (tab === 'stripe') {
      this.addControlsForm(this.keyValuePairsTab3, tab);
      const form = this.form.value;
      client = {
        rates: {
          ...this.data.client.rates,
          stripe: form.stripe,
        },
      };
      this.keyValuePairsTab3.forEach((pair) => {
        pair.key.setValue('');
        pair.value.setValue('');
      });
      this.keyValuePairsTab3 = [];
    }

    this.clientService.updateClientService(this.data.client.client_id, client).subscribe(
      (data) => {
        this.snackService.genericSnackBar('Client successfully updated', ['success-snackbar'], 5000);
      },
      (error) => {
        console.error(error);
        this.snackService.genericSnackBar('An error occurred updating client', ['error-snackbar'], 5000);
      }
    );
    this.isLoading = false;
  }

  cancel(tab: string, index: number): void {
    if (tab === 'billable') {
      this.keyValuePairsTab1.splice(index, 1);
    } else if (tab === 'medicare') {
      this.keyValuePairsTab2.splice(index, 1);
    } else if (tab === 'stripe') {
      this.keyValuePairsTab3.splice(index, 1);
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
